import { VueRenderer } from '@tiptap/vue-3'
import { computePosition, flip, offset, shift } from '@floating-ui/dom'
import MentionList from './MentionList.vue'
import { resourceApi } from '@/services/api'

export type MentionablePerson = {
  _id: string
  firstname?: string
  lastname?: string
  picture?: string | null
  display?: string
}

const updatePopover = (popover: HTMLElement | null, clientRect: () => DOMRect | null) => {
  if (!popover) return
  const rect = clientRect()
  if (!rect) return
  const reference = {
    getBoundingClientRect: () => rect,
  }
  computePosition(reference as any, popover, {
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
  }).then(({ x, y }) => {
    Object.assign(popover.style, {
      left: `${x}px`,
      top: `${y}px`,
    })
  })
}

const matchesQuery = (person: MentionablePerson, query: string) => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const haystack = [
    person.display,
    person.firstname,
    person.lastname,
    `${person.firstname || ''} ${person.lastname || ''}`,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(q)
}

const createRender = () => {
  let component: VueRenderer | undefined
  let popover: HTMLElement | null = null

  return {
    onStart: (props: any) => {
      component = new VueRenderer(MentionList, {
        props,
        editor: props.editor,
      })

      if (!props.clientRect) return

      popover = document.createElement('div')
      popover.style.position = 'absolute'
      popover.style.zIndex = '1000'
      popover.appendChild(component.element as Node)
      document.body.appendChild(popover)

      updatePopover(popover, props.clientRect)
    },

    onUpdate(props: any) {
      component?.updateProps(props)

      if (!props.clientRect) return

      updatePopover(popover, props.clientRect)
    },

    onKeyDown(props: any) {
      if (props.event.key === 'Escape') {
        popover?.remove()
        popover = null
        return true
      }

      return (component?.ref as { onKeyDown?: (p: any) => boolean } | null)?.onKeyDown?.(props)
    },

    onExit() {
      popover?.remove()
      popover = null
      component?.destroy()
    },
  }
}

/** Factory so callers can restrict @ suggestions (e.g. conversation members only). */
export const createMentionSuggestion = (
  getMentionables?: () => MentionablePerson[] | null | undefined,
) => ({
  items: async ({ query }: { query: string }) => {
    const restricted = getMentionables?.()
    // Array (even empty) = scoped mode; undefined = global resource search
    if (Array.isArray(restricted)) {
      return restricted
        .filter(person => person?._id && matchesQuery(person, query))
        .slice(0, 8)
    }

    const resources = await resourceApi.filter({ display: query, limit: 5, page: 0 }).promise
    return resources.docs
  },
  render: createRender,
})

export default createMentionSuggestion()
